import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiConfiguration } from '../api/api.configuration';
import { SessionStoreKey } from '../storage/session-store-key.enum';
import { SessionStore } from '../storage/storage.interface';
import { StorageService } from '../storage/storage.service';
import { HttpData } from './http-data.interface';
import { IWindowWithHttp } from './http-window.interface';

export class HttpService {

    /**
     * Get HttpService instance
     */
    public static getInstance(configuration: ApiConfiguration): HttpService {
        const windowInstance = this.getDOMInstance();

        if (windowInstance && windowInstance.configuration.clientId === configuration.clientId) {
            return windowInstance;
        }

        if (!this.instance) {
            this.instance = new HttpService(configuration);
            (window as IWindowWithHttp & typeof globalThis).DWSHttpInstance = this.instance;
        }

        return this.instance;
    }

    /** instance holder */
    private static instance: HttpService;

    private static xsrfTokenId: string = 'X-XSRF-TOKEN';

    private static getDOMInstance(): HttpService | undefined {
        const win: IWindowWithHttp = window as IWindowWithHttp & typeof globalThis;

        if (!win) {
            return undefined;
        }

        /* tslint:disable-next-line:no-string-literal */
        if (win['DWSHttpInstance'] === undefined) {
            return undefined;
        }

        /* tslint:disable-next-line:no-string-literal */
        return win['DWSHttpInstance'];
    }

    /** Response handler (error) */
    private static handleResponse(error: any): any {

        // if (error.response) {
        //     `Error with ${error.response.status} recieved for reason: ${error.response.statusText}`;
        // }

        // if (error.request) {
        //     `Request was made, but no response received.`;
        // }

        return error;
    }

    private requestToken: string | undefined;

    constructor(private configuration: ApiConfiguration) {
        this.requestToken = StorageService
            .getInstance({ poolId: this.configuration.clientId })
            .getRequestToken();

        if (this.requestToken) {
            Axios.defaults.headers[HttpService.xsrfTokenId] = this.requestToken;
        }
    }

    /**
     * HttpClient get method
     * @param url string
     * @param config AxiosRequestConfig (optional)
     * @return Promise<AxiosResponse>
     */
    public async get<T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        let response;

        try {
            response = await Axios.get(url, config);
            await this.updateXSRFtoken(response);
        } catch (error) {
            const eResponse: AxiosError = <AxiosError> error;

            if (eResponse.response) {
                return Promise.reject(eResponse.response);
            }

            if (eResponse.request) {
                return Promise.reject(eResponse.request);
            }

            return Promise.reject(eResponse.message);
        }

        return response;
    }

    /**
     * HttpClient delete method
     * @param url
     * @param config
     */
    public async delete<T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        let response;

        try {
            response = await Axios.delete(url, config);
            await this.updateXSRFtoken(response);
        } catch (error) {
            const axiosError: AxiosError = error as AxiosError;

            if (axiosError.response && axiosError.config && this.isInvalidXCSRFon(axiosError.response)) {
                response = this.retryInvalidCSRF(axiosError);
            } else {
                response = Promise.reject(HttpService.handleResponse(error));
            }
        }

        return response;
    }

    /**
     * HttpClient post method
     * @param url
     * @param data
     * @param config
     */
    public async post<T>(url: string, data: HttpData | string = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        let response;

        try {
            response = await Axios.post(url, data, config);
            await this.updateXSRFtoken(response);
        } catch (error) {
            const axiosError: AxiosError = error as AxiosError;

            if (axiosError.response && axiosError.config && this.isInvalidXCSRFon(axiosError.response)) {
                response = this.retryInvalidCSRF(axiosError);
            } else {
                response = Promise.reject(HttpService.handleResponse(error));
            }
        }

        return response;
    }

    /**
     * HttpClient put method
     * @param url
     * @param data
     * @param config
     */
    public async put(url: string, data: HttpData = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
        let response;

        try {
            response = await Axios.put(url, data, config);
            await this.updateXSRFtoken(response);
        } catch (error) {
            const axiosError: AxiosError = error as AxiosError;

            if (axiosError.response && axiosError.config && this.isInvalidXCSRFon(axiosError.response)) {
                response = this.retryInvalidCSRF(axiosError);
            } else {
                response = Promise.reject(HttpService.handleResponse(error));
            }
        }

        return response;
    }

    /**
     * HttpClient patch method
     * @param url
     * @param data
     * @param config
     */
    public async patch(url: string, data: HttpData = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> {
        let response;

        try {
            response = await Axios.patch(url, data, config);
            await this.updateXSRFtoken(response);
        } catch (error) {
            const axiosError: AxiosError = error as AxiosError;

            if (axiosError.response && axiosError.config && this.isInvalidXCSRFon(axiosError.response)) {
                response = this.retryInvalidCSRF(axiosError);
            } else {
                response = Promise.reject(HttpService.handleResponse(error));
            }
        }

        return response;
    }

    private isInvalidXCSRFon(response: AxiosResponse): boolean {
        return Object.entries(response.headers).filter((entry: [string, any]) => {
            if (entry[0] === 'x-xsrf-token-invalid') {
                return entry[1] === '1';
            }

            return false;
        }).length > 0;
    }

    private async retryInvalidCSRF(errorRequest: AxiosError): Promise<any> {
        if (!errorRequest) {
            return Promise.reject('missing request');
        }

        if (!errorRequest.response) {
            return Promise.reject('Missing response');
        }

        await this.updateXSRFtoken(errorRequest!.response);

        try {
            return await Axios.request(errorRequest.config);
        } catch (e) {
            return Promise.reject(HttpService.handleResponse(e));
        }
    }

    private async updateXSRFtoken(response: AxiosResponse): Promise<void> {
        const token = response!.headers[HttpService.xsrfTokenId.toLowerCase()];
        if (token === undefined) {
            return undefined;
        }

        if (token !== this.requestToken) {
            const storage: StorageService = StorageService.getInstance({ poolId: this.configuration.clientId });
            const session: SessionStore = storage.loadSession() || {} as SessionStore;

            this.requestToken = token;
            Axios.defaults.headers[HttpService.xsrfTokenId] = this.requestToken;

            if (session && session[SessionStoreKey.Common] !== undefined) {
                session![SessionStoreKey.Common]!.requestToken = this.requestToken;
            }

            if (session && session[SessionStoreKey.Common] === undefined) {
                session[SessionStoreKey.Common] = {
                    requestToken: this.requestToken,
                };
            }

            storage.saveSession(session);
        }

        return undefined;
    }

}
